/**
 * virtual-orchestra.js — window.VOrch
 *
 * Движок «Виртуального оркестра»: партии → один сведённый трек.
 *
 * Партия это либо ЗАПИСЬ, либо НОТЫ с выбранным инструментом. Дальше обе живут
 * одинаково: превращаются в буфер, получают сетку долей, подстраиваются под
 * опорную и сводятся.
 *
 * ── Чужая работа, которую здесь НЕ переписывают ───────────────────────────
 *   AudioDecoderRobust  чтение файла в PCM (HE-AAC у Яндекса и прочие ловушки)
 *   BeatGrid            сетка долей, держащаяся записи до конца
 *   AudioAlign          грубое смещение между двумя записями
 *   PartAlign           РЕШЕНИЕ, что с партией сделать, и причина
 *   WarpMap             кривая натяжения
 *   SignalsmithWorkerEngine   само натяжение, тон при этом не уезжает
 *   NoteRender          ноты → звук сэмплами FluidR3
 *   AudioMixdown        сведение (14× быстрее графа FFmpeg)
 *   AudioPipeline       WAV/MP3 на выходе
 *
 * Здесь остаётся только то, чего нет ни у кого: порядок этих шагов и честный
 * рассказ о том, что сделано с каждой партией.
 */
(function (w) {
    'use strict';

    var SLUG = 'virtual-orchestra';
    /* Дольше этого партию не берём: дело не в принципе, а в памяти вкладки —
       натяжение держит вход и выход разом. */
    var MAX_PART_MIN = 12;
    /* Частота сведения. 44.1 кГц — то, в чём живут наши сэмплы; гнать выше
       незачем, а ниже значит терять верх у записей. */
    var SR = 44100;

    /**
     * Сколько ВСЕГО звука тул готов держать, в секундах.
     *
     * ⚠️ Ограничение на ОДНУ партию есть у общей двери декодирования, а на все
     * вместе — не было ни у кого, и это убивало бы тул ровно там, где он
     * нужнее всего: на телефоне. Считаем честно. Секунда моно float32 при
     * 44.1 кГц — 176 КБ; во время сборки в памяти живут исходные буферы,
     * натянутые копии и микс, то есть примерно ТРОЙНОЙ объём. Потолок вкладки
     * на iPhone — 500…700 МБ (замер в CLAUDE.md), и занимать его целиком
     * звуком нельзя: там же модель сэмплов, волны и сама страница.
     *
     * Отсюда: берём около 6 % оперативной памяти устройства под рабочий объём
     * и делим на три. На 4 ГБ выходит ~7 минут суммарно, на 8 ГБ ~15.
     * Границы [5, 30] — чтобы устройство, не назвавшее память, не получило ни
     * абсурдно мало, ни абсурдно много.
     */
    function budgetSec() {
        var gb = (typeof navigator !== 'undefined' && navigator.deviceMemory) ? navigator.deviceMemory : 4;
        var workingBytes = gb * 1024 * 1024 * 1024 * 0.06;
        var sec = workingBytes / 3 / (SR * 4);
        return Math.max(300, Math.min(1800, Math.round(sec)));
    }

    /** Сколько звука уже набрано. */
    function totalSec(parts) {
        var s = 0;
        for (var i = 0; i < (parts || []).length; i++) {
            if (parts[i] && parts[i].buffer) s += parts[i].buffer.duration;
        }
        return s;
    }

    var _ctx = null;
    function ctx() {
        if (!_ctx) _ctx = new (w.AudioContext || w.webkitAudioContext)({ sampleRate: SR });
        return _ctx;
    }

    function fail(code, extra) {
        var e = new Error(code);
        if (extra) { for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) e[k] = extra[k]; }
        return e;
    }

    /* ── Чтение файла ─────────────────────────────────────────────────────── */

    function isMidiFile(file) {
        return /\.(mid|midi)$/i.test(file.name || '') || /audio\/(midi|x-midi)/.test(file.type || '');
    }

    /**
     * Ноты из .mid. Разборщик отдаёт плоский список со всех дорожек — для
     * ПАРТИИ это и нужно: один файл здесь звучит одним инструментом, который
     * человек выбирает сам.
     *
     * Канал 9 в General MIDI — ударные. Если нот там большинство, партия
     * помечается ударной, и инструмент у неё не спрашивается: у удара своя
     * высота не при чём.
     */
    function readMidi(file) {
        if (!w.TimbricaMidiParser) return Promise.reject(fail('midi_parser_missing'));
        return file.arrayBuffer().then(function (buf) {
            var parsed = w.TimbricaMidiParser.parse(new Uint8Array(buf));
            var raw = (parsed && parsed.notes) || [];
            if (!raw.length) throw fail('notes_empty', { name: file.name });
            var drums = 0;
            var notes = raw.map(function (n) {
                if (n.channel === 9) drums++;
                return {
                    pitchMidi: n.midi,
                    startTimeSeconds: n.startMs / 1000,
                    durationSeconds: Math.max(0.05, (n.endMs - n.startMs) / 1000),
                    amplitude: n.velocity
                };
            });
            return { notes: notes, percussion: drums > raw.length / 2, bpm: parsed.bpm || null };
        });
    }

    /**
     * Чтение записи в PCM.
     *
     * ⚠️ Дверь именно decodeGated, а не robust: этот тул держит ВЕСЬ буфер, да
     * ещё и не один — а gated сперва узнаёт длину, потом спрашивает гейт
     * (тировая стена, потолок устройства, предупреждение для телефона) и лишь
     * потом декодирует. Порядок и есть починка: до него 69 блейдов
     * декодировали, а смотрели на длину после.
     *
     * Отказ гейта — не ошибка: диалог уже показан человеку, партию просто не
     * добавляем.
     */
    function decodeAudio(file) {
        var c = ctx();
        var D = w.AudioDecoderRobust;
        if (D && typeof D.decodeGated === 'function') {
            return D.decodeGated(file, { slug: SLUG, ctx: c }).then(function (r) {
                if (r && r.refused) throw fail('refused_' + r.refused, { name: file.name, refused: r.refused });
                var b = (r && r.buffer) ? r.buffer : r;
                if (!b || !b.length) throw fail('decode_failed', { name: file.name });
                return b;
            });
        }
        if (D && typeof D.robust === 'function') {
            return D.robust(file, c, { slug: SLUG }).then(function (r) {
                var b = (r && r.buffer) ? r.buffer : r;
                if (!b || !b.length) throw fail('decode_failed', { name: file.name });
                return b;
            });
        }
        return file.arrayBuffer().then(function (buf) { return c.decodeAudioData(buf); });
    }

    /* ── Разбор партии ────────────────────────────────────────────────────── */

    /**
     * Сетка долей и темп. Для партии из нот звук сперва синтезируется: так обе
     * ветки меряются ОДНИМ прибором, и сравнивать их между собой честно.
     */
    function analyze(part) {
        if (!part.buffer) return Promise.resolve(part);
        var mins = part.buffer.duration / 60;
        if (mins > MAX_PART_MIN) return Promise.reject(fail('too_long', { min: MAX_PART_MIN }));
        try {
            var g = w.BeatGrid ? w.BeatGrid.track(part.buffer) : null;
            part.beats = (g && g.beats) || [];
            part.bpm = (g && g.bpm) || null;
            part.alignment = (g && g.alignment) || 0;
        } catch (e) {
            part.beats = []; part.bpm = null; part.alignment = 0;
        }
        return Promise.resolve(part);
    }

    /**
     * Уровень озвученных нот — до слышимого.
     *
     * ⚠️⚠️ Замер 18.09.2026, круговой прогон «ноты → звук → ноты»: гамма,
     * озвученная роялем, выходит на **−24.9 dBFS по пику и −39.9 по СКЗ**,
     * тогда как обычная запись, которую в этот же тул приносят рядом, идёт на
     * −1.9 и −16.5. Разрыв **23 дБ**, а ползунок громкости партии даёт
     * максимум +3 дБ (1.4×). То есть партию из нот, поставленную рядом с
     * записью, услышать БЫЛО НЕЛЬЗЯ ничем — при том что тул рапортовал об
     * успехе, ноты звучали верные и файл собирался.
     *
     * Причина не в нашей арифметике (`NoteRender` честно множит velocity на
     * 0.8), а в самих сэмплах: библиотеки General MIDI намеренно записаны тихо,
     * чтобы 128 одновременных голосов не выбили потолок. Это артефакт
     * БИБЛИОТЕКИ, а не музыкальное решение автора нот, — сохранять его незачем,
     * сохранять нечего.
     *
     * Поэтому партия приводится к пику 0.7 (−3.1 dBFS) ОДНИМ множителем:
     * внутренняя динамика между нотами сохраняется точно, меняется только то,
     * с какого уровня партия приходит в микс. Тишину не трогаем — усиливать
     * там нечего, а делить на ноль нельзя.
     */
    var NOTES_TARGET_PEAK = 0.7;

    function normalizeRendered(buf) {
        if (!buf || !buf.length) return buf;
        var peak = 0, c, i, d;
        for (c = 0; c < buf.numberOfChannels; c++) {
            d = buf.getChannelData(c);
            for (i = 0; i < d.length; i++) { var v = d[i] < 0 ? -d[i] : d[i]; if (v > peak) peak = v; }
        }
        /* Ниже этого — не партия, а тишина или шум: усиливать нечего. */
        if (!(peak > 1e-4)) return buf;
        var k = NOTES_TARGET_PEAK / peak;
        if (Math.abs(k - 1) < 0.01) return buf;
        for (c = 0; c < buf.numberOfChannels; c++) {
            d = buf.getChannelData(c);
            for (i = 0; i < d.length; i++) d[i] *= k;
        }
        return buf;
    }

    /**
     * Набор ударных для партии с канала 9.
     *
     * ⚠️⚠️ Без него `NoteRender` МОЛЧА пропускает ударный голос — в его коде
     * стоит `if (!drumKit) continue`, то есть партия превращается в ТИШИНУ, а
     * тул при этом рапортует об успехе: ноты разобраны, партия на шкале, файл
     * собран. Так и было до 18.09.2026: замер краевым прогоном показал 4 ноты
     * разобрано, ударные распознаны, пик **0**.
     *
     * Цена ошибки здесь выше обычной, потому что FAQ этого же тула зовёт
     * приносить ритм из «Виртуальных барабанов», а тот отдаёт ровно канал 9:
     * обещанный нами путь кончался тишиной.
     */
    function loadDrumKit(notes) {
        if (!w.GmDrumKit || typeof w.GmDrumKit.load !== 'function') return Promise.resolve(null);
        var want = [], seen = {};
        for (var i = 0; i < (notes || []).length; i++) {
            var m = notes[i].pitchMidi;
            if (!seen[m]) { seen[m] = 1; want.push(m); }
        }
        if (!want.length) return Promise.resolve(null);
        return w.GmDrumKit.load(ctx(), want).then(function (kit) {
            return (kit && Object.keys(kit).length) ? kit : null;
        }).catch(function () { return null; });
    }

    /** Ноты → буфер выбранным инструментом. */
    function renderNotes(part) {
        if (!w.NoteRender) return Promise.reject(fail('note_render_missing'));
        var voice = part.percussion
            ? { percussion: true, notes: part.notes }
            : { instrument: part.instrument, notes: part.notes };
        var names = part.percussion ? [] : [part.instrument];
        if (!part.percussion && !w.NoteRender.isHosted(part.instrument)) {
            return Promise.reject(fail('instrument_missing', { name: part.instrument }));
        }
        var kitP = part.percussion ? loadDrumKit(part.notes) : Promise.resolve(null);
        return Promise.all([
            w.NoteRender.load(ctx(), names, { only: w.NoteRender.keysFor(part.notes) }),
            kitP
        ])
            .then(function (both) {
                var kit = both[1];
                /* ⚠️ Отсутствующий набор — это ОТКАЗ, а не тихая партия.
                   Молчание выглядит как успех и обнаруживается уже в скачанном
                   файле; названная причина обнаруживается сразу. */
                if (part.percussion && !kit) throw fail('drumkit_missing');
                return w.NoteRender.render([voice], {
                    sampleRate: SR,
                    instruments: both[0],
                    drumKit: kit
                });
            })
            .then(normalizeRendered);
    }

    /* ── Решение по каждой партии ─────────────────────────────────────────── */

    /** Грубое смещение партии относительно опоры. */
    function coarseOffset(part, anchor) {
        if (!w.AudioAlign || !part.buffer || !anchor.buffer) return 0;
        try {
            var r = w.AudioAlign.findOffset(
                part.buffer.getChannelData(0), part.buffer.sampleRate,
                anchor.buffer.getChannelData(0), anchor.buffer.sampleRate
            );
            /* Ниже порога уверенности НЕ двигаем: неверный автосдвиг хуже, чем
               отсутствие сдвига (та же логика, что в /voice-cover). */
            if (!r || !r.locked) return 0;
            return -r.offsetSec;
        } catch (e) { return 0; }
    }

    /**
     * Сколько ЗАТАКТА просит кривая: у партии, вступающей позже, звук до первой
     * сопоставленной доли длиннее, чем место перед соответствующей долей
     * опоры. Отрицательное «где окажется начало партии» и есть эта нехватка.
     */
    function leadOf(plan) {
        if (!plan || plan.mode !== 'warp' || !plan.warp || plan.warp.length < 2) return 0;
        var a = plan.warp[0], b = plan.warp[1];
        var dOut = b[1] - a[1];
        if (!(dOut > 0)) return 0;
        var rate0 = (b[0] - a[0]) / dOut;
        if (!(rate0 > 0)) return 0;
        var outAtZero = a[1] - a[0] / rate0;
        return outAtZero < 0 ? -outAtZero : 0;
    }

    /**
     * Что сделать с каждой партией, кроме опорной. Возвращает план на партию —
     * с режимом, причиной и числом «ради чего».
     *
     * ⚠️⚠️ ЗАТАКТ. Партия, вступающая позже опоры, не помещается в выход,
     * который начинается в нуле: её голова длиннее, чем место перед первой
     * общей долей. Отказаться здесь значит отказаться почти всегда — это
     * поймал стенд на точной истине (копия записи, замедленная на 4 % и
     * сдвинутая на 0.35 с): кривая отвергалась с `head_does_not_fit`, партия
     * молча падала на ступень «только сдвиг», и копии складывались с
     * коэффициентом 1.43 вместо 2.0, то есть не складывались вовсе.
     *
     * Правильный ответ тот же, что в любой студии: дать всей композиции
     * затакт. Вся дорожка съезжает на общую величину, доли остаются на своих
     * местах друг относительно друга, а слышно это как долю секунды тишины
     * перед началом.
     */
    function planAll(parts, anchorId) {
        var anchor = null, i;
        for (i = 0; i < parts.length; i++) if (parts[i].id === anchorId) anchor = parts[i];
        if (!anchor) return {};
        var plans = {};
        plans[anchor.id] = { mode: 'anchor', reason: 'anchor', offsetSec: 0 };
        for (i = 0; i < parts.length; i++) {
            var p = parts[i];
            if (p.id === anchorId || !p.buffer) continue;
            var off = (typeof p.manualOffset === 'number') ? p.manualOffset : coarseOffset(p, anchor);
            plans[p.id] = w.PartAlign.decide({
                partBeats: p.beats, anchorBeats: anchor.beats,
                bpmPart: p.bpm, bpmAnchor: anchor.bpm,
                alignment: p.alignment, anchorAlignment: anchor.alignment,
                offsetSec: off
            });
        }

        /* Затакт — общий на всю композицию: самый требовательный задаёт его
           всем, иначе партии разъедутся между собой. */
        var lead = 0, k;
        for (k in plans) if (Object.prototype.hasOwnProperty.call(plans, k)) {
            var need = leadOf(plans[k]);
            if (need > lead) lead = need;
        }
        /* Отрицательные сдвиги ступени «только сдвиг» тоже просят затакта:
           партия, которую надо начать РАНЬШЕ нуля, без него просто теряет
           голову. Ручной сдвиг человека здесь такой же полноправный источник
           требования, как и расчёт: увёл партию назад — значит нужен затакт. */
        for (k in plans) if (Object.prototype.hasOwnProperty.call(plans, k)) {
            var pnudge = 0;
            for (i = 0; i < parts.length; i++) if (String(parts[i].id) === String(k)) pnudge = parts[i].nudgeSec || 0;
            var neg = -((plans[k].offsetSec || 0) + pnudge);
            if (neg > lead) lead = neg;
        }
        if (lead > 0) {
            for (k in plans) if (Object.prototype.hasOwnProperty.call(plans, k)) {
                var pl = plans[k];
                pl.leadSec = lead;
                if (pl.mode === 'warp' && pl.warp) {
                    pl.warp = pl.warp.map(function (pr) { return [pr[0], pr[1] + lead]; });
                } else {
                    pl.offsetSec = (pl.offsetSec || 0) + lead;
                }
            }
        }
        return plans;
    }

    /* ── Натяжение ────────────────────────────────────────────────────────── */

    /**
     * Подпись «во что превратится звук партии».
     *
     * Только то, что МЕНЯЕТ САМИ СЭМПЛЫ: режим и кривая натяжения. Смещение,
     * громкость, панорама и петля сюда НЕ входят — они решаются на этапе
     * сведения и воспроизведения, и включать их в ключ значило бы пересчитывать
     * натяжение от каждого движения ползунка громкости.
     */
    function stretchKey(part, plan) {
        if (!plan) return 'raw';
        if (plan.mode === 'warp' && plan.warp && plan.warp.length > 1) {
            var wp = plan.warp, a = wp[0], b = wp[wp.length - 1];
            return 'warp:' + wp.length + ':' + a[0].toFixed(4) + ',' + a[1].toFixed(4)
                + ':' + b[0].toFixed(4) + ',' + b[1].toFixed(4);
        }
        if (plan.mode === 'rate' && plan.rate) return 'rate:' + plan.rate.toFixed(6);
        return 'raw';
    }

    /**
     * Звук партии после натяжения.
     *
     * ⚠️ Результат КЭШИРУЕТСЯ на самой партии. Натяжение — единственный дорогой
     * шаг в туле (переразложение всего сигнала), и до кэша он повторялся при
     * каждой сборке: человек, собравший трек, поправивший громкость и собравший
     * снова, платил за одно и то же второй раз. Тот же кэш делает возможным
     * ЖИВОЕ прослушивание: транспорт играет ровно те буферы, которые попадут в
     * файл, а не их приблизительную замену. Ключ — только то, что меняет
     * сэмплы (см. stretchKey), поэтому громкость, панорама и сдвиг кэш не
     * сбрасывают.
     */
    function applyPlan(part, plan) {
        var key = stretchKey(part, plan);
        if (key === 'raw') return Promise.resolve(part.buffer);
        if (part._stretchKey === key && part._stretched) return Promise.resolve(part._stretched);
        var eng = w.SignalsmithWorkerEngine;
        if (!eng) return Promise.resolve(part.buffer);
        var keep = function (buf) { part._stretchKey = key; part._stretched = buf; return buf; };
        if (plan.mode === 'warp') {
            return eng.shift(part.buffer, { warp: plan.warp }).then(keep).catch(function (e) {
                /* Кривая не прошла — не молчим и не роняем сборку: партия
                   остаётся на ступень ниже, а причина уезжает в план. */
                plan.mode = 'offset';
                plan.reason = 'warp_refused';
                plan.refusedAs = String((e && e.message) || e);
                return part.buffer;
            });
        }
        if (plan.mode === 'rate') {
            return eng.shift(part.buffer, { rate: plan.rate }).then(keep).catch(function () {
                plan.mode = 'offset';
                plan.reason = 'rate_failed';
                return part.buffer;
            });
        }
        return Promise.resolve(part.buffer);
    }

    /** Исходный звук партии сменился (другой инструмент, другой файл) — кэш недействителен. */
    function dropStretch(part) {
        if (!part) return;
        part._stretchKey = null; part._stretched = null;
    }

    /* ── Описание микса ───────────────────────────────────────────────────
       ОДНО описание «что такое этот микс» — и три потребителя: сборка в файл,
       живое прослушивание и картинка на шкале. До этого длину трека считала
       только сборка, у себя внутри; шкала не знала её вовсе, а прослушивания
       не было. Три расчёта одного и того же расходятся молча — здесь он один.
       ──────────────────────────────────────────────────────────────────── */

    /**
     * Заглушена ли партия.
     *
     * ⚠️⚠️ СОЛО и ЗАГЛУШЕНИЕ — разные по сроку жизни вещи, и путать их опасно.
     * «M» — решение о СОДЕРЖАНИИ: этой партии в треке не будет. «S» — способ
     * ПОСЛУШАТЬ одну на фоне остальных, и он по смыслу временный: человек
     * включает соло, проверяет, что скрипка встала в такт, и идёт нажимать
     * «Собрать трек». Если бы соло доходило до файла, он получил бы трек из
     * одной скрипки — молча, без единого слова о том, что случилось, и узнал
     * бы об этом уже скачав.
     *
     * Поэтому соло учитывается ТОЛЬКО при прослушивании (`useSolo`), а в файл
     * едет всё, что не заглушено явно.
     */
    function effectiveMute(parts, part, useSolo) {
        if (part.muted) return true;
        if (!useSolo) return false;
        for (var i = 0; i < parts.length; i++) if (parts[i].solo) return !part.solo;
        return false;
    }

    /** Звук партии, который пойдёт в микс: натянутый, если натяжение уже посчитано. */
    function voiceBuffer(part, plan) {
        var key = stretchKey(part, plan);
        if (key !== 'raw' && part._stretchKey === key && part._stretched) return part._stretched;
        return part.buffer;
    }

    /**
     * Дорожка для AudioMixdown — она же описание голоса для прослушивания.
     * `buf` передаётся явно: у сборки это натянутый буфер, у предпросмотра до
     * натяжения — исходный, и подменять его втихую внутри нельзя.
     */
    function trackSpec(parts, part, plan, buf, useSolo) {
        var off = ((plan && plan.offsetSec) || 0) + (part.nudgeSec || 0);
        return {
            id: part.id,
            buffer: buf || part.buffer,
            volume: (typeof part.volume === 'number') ? part.volume : 1,
            muted: effectiveMute(parts, part, useSolo),
            pan: Math.max(-1, Math.min(1, +part.pan || 0)),
            offsetSec: Math.max(0, off),
            trimStartSec: 0, trimEndSec: 0,
            fadeInSec: 0, fadeOutSec: 0,
            /* Петля: барабанные четыре такта под трёхминутный трек — самый
               частый случай сборки, и без неё партия звучит один раз и
               замолкает. Короткое перекрёстное затухание убирает щелчок. */
            loop: !!part.loop,
            crossfadeSec: part.loop ? 0.02 : 0,
            speed: 1
        };
    }

    /**
     * Длина трека. Задают её НЕзацикленные партии: зацикленная по определению
     * тянется столько, сколько надо, и если считать по ней, трек рос бы до
     * бесконечности при первой же петле.
     */
    function mixDuration(tracks) {
        var dur = 0, i, end;
        for (i = 0; i < tracks.length; i++) {
            if (tracks[i].loop) continue;
            end = tracks[i].offsetSec + (tracks[i].buffer ? tracks[i].buffer.duration : 0);
            if (end > dur) dur = end;
        }
        if (dur <= 0) {
            for (i = 0; i < tracks.length; i++) {
                end = tracks[i].offsetSec + (tracks[i].buffer ? tracks[i].buffer.duration : 0);
                if (end > dur) dur = end;
            }
        }
        return dur;
    }

    /** Полное описание микса из текущего состояния партий. */
    function mixSpec(parts, plans, opts) {
        opts = opts || {};
        var tracks = [];
        for (var i = 0; i < parts.length; i++) {
            var p = parts[i];
            if (!p.buffer) continue;
            var plan = (plans && plans[p.id]) || { mode: 'manual', offsetSec: 0 };
            tracks.push(trackSpec(parts, p, plan, opts.stretched ? voiceBuffer(p, plan) : p.buffer, opts.solo));
        }
        return { tracks: tracks, durationSec: mixDuration(tracks) };
    }

    /* ── Сборка ───────────────────────────────────────────────────────────── */

    /**
     * Все партии → один буфер. Сведение нативное: тот же расчёт, что у
     * FFmpeg-графа, но в браузере и на порядок быстрее.
     */
    function build(parts, plans, opts) {
        opts = opts || {};
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};
        var done = 0, total = parts.length || 1;
        var stretched = {};

        return parts.reduce(function (chain, part) {
            return chain.then(function () {
                if (!part.buffer) { done++; return; }
                var plan = plans[part.id] || { mode: 'manual', offsetSec: 0 };
                return applyPlan(part, plan).then(function (buf) {
                    stretched[part.id] = buf;
                    done++;
                    onProgress(done / total);
                });
            });
        }, Promise.resolve()).then(function () {
            /* Дорожки собираются ПОСЛЕ натяжения — из того же описания, что
               видит шкала и слышит предпросмотр. Расхождение «на картинке одно,
               в файле другое» здесь структурно невозможно. */
            var tracks = [];
            for (var i = 0; i < parts.length; i++) {
                var p = parts[i];
                if (!p.buffer) continue;
                var plan = plans[p.id] || { mode: 'manual', offsetSec: 0 };
                tracks.push(trackSpec(parts, p, plan, stretched[p.id] || p.buffer, false));
            }
            if (!tracks.length) throw fail('nothing_to_mix');
            var dur = mixDuration(tracks);
            var plan2 = w.AudioMixdown.plan({
                tracks: tracks, durationSec: dur, sampleRate: SR, channels: 2
            });
            return w.AudioMixdown.render({ tracks: tracks, plan: plan2 }, { signal: opts.signal });
        }).then(function (buf) {
            /* ⚠️ Сумма партий выходит за потолок штатно: две дорожки по
               отдельности тихие вместе дают больше единицы. Web Audio это
               терпит (float), а int16 на выдаче — нет, и человек слышит «в
               браузере чисто, в файле треск». Домашний примитив давит только
               окна перегруза, а не топит весь микс. Замер стенда до него:
               пик ровно 1.0, то есть в потолке. */
            if (w.ExportCeiling && typeof w.ExportCeiling.apply === 'function') {
                return w.ExportCeiling.apply(buf, { slug: SLUG }).then(function (r) {
                    return (r && r.buffer) ? r.buffer : buf;
                }).catch(function () { return buf; });
            }
            return buf;
        });
    }

    /* ── Живое прослушивание ──────────────────────────────────────────────
       Услышать, СОШЛИСЬ ли партии, — единственный способ понять, сработал ли
       тул. До этого услышать можно было только СОБРАННЫЙ трек, то есть
       потратив суточный запуск; человек платил запуском за ответ на вопрос
       «а оно вообще сошлось?». Здесь тот же микс играется живьём и ничего не
       стоит: файла на выходе нет, значит и списывать нечего.

       Играются ТЕ ЖЕ буферы, что попадут в файл (кэш натяжения в applyPlan),
       поэтому прослушивание — не приблизительная замена, а сам результат.
       ──────────────────────────────────────────────────────────────────── */

    function createTransport() {
        var voices = [];      /* { id, src, gain, panner, loop } */
        var clicks = [];
        var playing = false, startCtxTime = 0, fromSec = 0, durSec = 0;
        var raf = 0, onTick = null, onEnd = null;
        var partsRef = [], plansRef = null;

        function now() { return ctx().currentTime; }

        function positionSec() {
            if (!playing) return fromSec;
            return fromSec + (now() - startCtxTime);
        }

        function stopNodes() {
            for (var i = 0; i < voices.length; i++) {
                try { voices[i].src.stop(); } catch (e) {}
                try { voices[i].src.disconnect(); } catch (e) {}
            }
            for (var c = 0; c < clicks.length; c++) {
                try { clicks[c].stop(); } catch (e) {}
            }
            voices = []; clicks = [];
        }

        function tick() {
            if (!playing) return;
            var t = positionSec();
            if (onTick) { try { onTick(Math.min(t, durSec), durSec); } catch (e) {} }
            if (t >= durSec) {
                var cb = onEnd;
                stop();
                if (cb) { try { cb(); } catch (e) {} }
                return;
            }
            raf = w.requestAnimationFrame(tick);
        }

        function stop() {
            if (!playing && !voices.length) { return; }
            playing = false;
            if (raf) { w.cancelAnimationFrame(raf); raf = 0; }
            stopNodes();
        }

        /**
         * Запуск. Возвращает промис живого контекста — жест берётся через
         * общую дверь: у Safari и Chrome на Android активация жеста умирает
         * вместе с обработчиком, и голое `resume()` после await там не проходит.
         */
        function play(parts, plans, o) {
            o = o || {};
            stop();
            partsRef = parts; plansRef = plans;
            onTick = typeof o.onTick === 'function' ? o.onTick : null;
            onEnd = typeof o.onEnd === 'function' ? o.onEnd : null;

            /* ⚠️⚠️ НАТЯНУТЬ ДО ТОГО, как планировать голоса.
               Здесь стояло `mixSpec(..., {stretched:true})` сразу — и это было
               ложью в самом чувствительном месте: на ПЕРВОМ нажатии кэш
               натяжения пуст, `voiceBuffer` честно отдаёт исходный буфер, и
               человек слышал партии в их собственном темпе, просто сдвинутые.
               То есть кнопка «Прослушать вместе» отвечала на вопрос «сошлось?»
               звуком НЕсошедшегося. Поймано не ухом, а числом: транспорт
               объявлял 14.91 с там, где микс выходит 14.36 — длину задавал
               ненатянутый буфер.

               Порядок шагов важен и он такой: сперва живой контекст (жест
               ЖИВЁТ только внутри обработчика — у Safari и Chrome на Android
               он умирает вместе с ним), и только потом долгое натяжение. */
            var gate = w.__liveAudioCtxReady ? w.__liveAudioCtxReady(ctx()) : Promise.resolve(ctx());
            return Promise.resolve(gate).then(function (live) {
                if (live && live !== _ctx) _ctx = live;
                var chain = Promise.resolve();
                parts.forEach(function (p) {
                    if (!p.buffer) return;
                    var pl = plans && plans[p.id];
                    if (!pl) return;
                    chain = chain.then(function () { return applyPlan(p, pl); });
                });
                if (typeof o.onPrepare === 'function') { try { o.onPrepare(); } catch (e) {} }
                return chain;
            }).then(function () {
                var spec = mixSpec(parts, plans, { stretched: true, solo: true });
                durSec = spec.durationSec;
                if (!(durSec > 0)) throw fail('nothing_to_play');
                fromSec = Math.max(0, Math.min(+o.fromSec || 0, Math.max(0, durSec - 0.05)));
                var c = ctx(), t0 = c.currentTime + 0.06;   /* запас на планирование */
                startCtxTime = t0;
                for (var i = 0; i < spec.tracks.length; i++) {
                    schedule(c, spec.tracks[i], t0);
                }
                if (o.click && o.beats && o.beats.length) scheduleClicks(c, o.beats, t0);
                playing = true;
                raf = w.requestAnimationFrame(tick);
                return true;
            });
        }

        function schedule(c, tr, t0) {
            if (!tr.buffer) return;
            var dur = tr.buffer.duration;
            var when = t0 + Math.max(0, tr.offsetSec - fromSec);
            var into = Math.max(0, fromSec - tr.offsetSec);
            if (!tr.loop && into >= dur) return;           /* партия уже отзвучала */
            if (tr.loop && dur > 0) into = into % dur;

            var src = c.createBufferSource();
            src.buffer = tr.buffer;
            src.loop = !!tr.loop;
            var g = c.createGain();
            g.gain.value = tr.muted ? 0 : tr.volume;
            var last = g;
            var panner = null;
            if (tr.pan && c.createStereoPanner) {
                panner = c.createStereoPanner();
                panner.pan.value = tr.pan;
                g.connect(panner); last = panner;
            }
            src.connect(g); last.connect(c.destination);
            try { src.start(when, into); } catch (e) { return; }
            voices.push({ id: tr.id, src: src, gain: g, panner: panner, loop: !!tr.loop });
        }

        /**
         * Щелчок метронома по долям ОПОРНОЙ партии.
         *
         * Он здесь не украшение: доли опорной — это и есть та сетка, к которой
         * тул тянет остальных, и услышать её вместе с миксом значит услышать,
         * СОШЛОСЬ ли. Один короткий тон на долю; предел в 4000 щелчков —
         * пятиминутный трек на 160 ударах это 800, а вырожденная сетка может
         * дать десятки тысяч узлов и подвесить вкладку планированием.
         */
        function scheduleClicks(c, beats, t0) {
            var n = 0;
            for (var i = 0; i < beats.length && n < 4000; i++) {
                var at = beats[i];
                if (at < fromSec) continue;
                var when = t0 + (at - fromSec);
                var osc = c.createOscillator(), g = c.createGain();
                osc.frequency.value = (i % 4 === 0) ? 1600 : 1100;
                g.gain.setValueAtTime(0.0001, when);
                g.gain.exponentialRampToValueAtTime(0.16, when + 0.002);
                g.gain.exponentialRampToValueAtTime(0.0001, when + 0.035);
                osc.connect(g); g.connect(c.destination);
                osc.start(when); osc.stop(when + 0.05);
                clicks.push(osc);
                n++;
            }
        }

        /** Громкость/заглушение/панорама на лету — без перезапуска. */
        function refresh() {
            if (!playing || !partsRef) return;
            for (var i = 0; i < voices.length; i++) {
                var v = voices[i], p = null;
                for (var j = 0; j < partsRef.length; j++) if (partsRef[j].id === v.id) p = partsRef[j];
                if (!p) {
                    /* Партию убрали во время прослушивания. Молчать она обязана
                       сразу: иначе человек слышит то, чего на экране уже нет. */
                    try { v.gain.gain.setTargetAtTime(0, ctx().currentTime, 0.01); } catch (e) {}
                    continue;
                }
                var vol = effectiveMute(partsRef, p, true) ? 0 : ((typeof p.volume === 'number') ? p.volume : 1);
                try { v.gain.gain.setTargetAtTime(vol, ctx().currentTime, 0.01); } catch (e) {}
                if (v.panner) { try { v.panner.pan.value = Math.max(-1, Math.min(1, +p.pan || 0)); } catch (e) {} }
            }
        }

        return {
            play: play, stop: stop, refresh: refresh,
            isPlaying: function () { return playing; },
            position: positionSec,
            duration: function () { return durSec; }
        };
    }

    var _transport = null;
    function transport() {
        if (!_transport) _transport = createTransport();
        return _transport;
    }

    w.VOrch = {
        SLUG: SLUG,
        budgetSec: budgetSec,
        totalSec: totalSec,
        MAX_PART_MIN: MAX_PART_MIN,
        SR: SR,
        ctx: ctx,
        isMidiFile: isMidiFile,
        readMidi: readMidi,
        decodeAudio: decodeAudio,
        renderNotes: renderNotes,
        loadDrumKit: loadDrumKit,
        normalizeRendered: normalizeRendered,
        NOTES_TARGET_PEAK: NOTES_TARGET_PEAK,
        analyze: analyze,
        coarseOffset: coarseOffset,
        planAll: planAll,
        applyPlan: applyPlan,
        dropStretch: dropStretch,
        stretchKey: stretchKey,
        effectiveMute: effectiveMute,
        voiceBuffer: voiceBuffer,
        trackSpec: trackSpec,
        mixDuration: mixDuration,
        mixSpec: mixSpec,
        transport: transport,
        build: build
    };
})(window);
